class ProductRecommendationsSlider {
  constructor(element) {
    this.element = element;
    this.productId = element.dataset.productId;
    this.sectionId = element.dataset.sectionId;
    this.limit = parseInt(element.dataset.limit) || 6;
    this.intent = element.dataset.intent || "related";

    this.loadingElement = document.getElementById(`loading-${this.sectionId}`);
    this.contentElement = document.getElementById(`content-${this.sectionId}`);
    this.emptyElement = document.getElementById(`empty-${this.sectionId}`);
    this.wrapperElement = document.getElementById(
      `recommendations-wrapper-${this.sectionId}`
    );

    this.swiper = null;

    this.init();
  }

  /**
   * Initialize the component
   */
  async init() {
    if (!this.productId) {
      this.showEmptyState();
      return;
    }

    try {
      await this.fetchRecommendations();
    } catch (error) {
      console.error("Error loading product recommendations:", error);
      this.showEmptyState();
    }
  }

  /**
   * Fetch product recommendations from Shopify's Ajax API
   */
  async fetchRecommendations() {
    const url = `${window.Shopify.routes.root}recommendations/products.json?product_id=${this.productId}&limit=${this.limit}&intent=${this.intent}`;

    try {
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.products && data.products.length > 0) {
        this.renderProducts(data.products);
        this.initSwiper();
        this.showContent();
      } else {
        this.showEmptyState();
      }
    } catch (error) {
      console.error("Failed to fetch recommendations:", error);
      throw error;
    }
  }

  /**
   * Render product cards
   */
  renderProducts(products) {
    this.wrapperElement.innerHTML = "";

    products.forEach((product) => {
      const slide = this.createProductSlide(product);
      this.wrapperElement.appendChild(slide);
    });
  }

  /**
   * Create a single product slide
   */
  createProductSlide(product) {
    const slide = document.createElement("div");
    slide.className = "swiper-slide";

    const productCard = document.createElement("a");
    productCard.href = product.url;
    productCard.className = "product-recommendations-slider__product-card";
    productCard.setAttribute("aria-label", `View ${product.title}`);

    // Product image
    const imageContainer = document.createElement("div");
    imageContainer.className = "product-recommendations-slider__product-image";

    const image = document.createElement("img");

    if (product.featured_image) {
      image.src = product.featured_image;
      image.alt = product.title;
    } else {
      // Placeholder image if no product image available
      image.src = `data:image/svg+xml;base64,${btoa('<svg width="100" height="100" xmlns="http://www.w3.org/2000/svg"><rect width="100" height="100" fill="#f3f3f3"/><text x="50" y="50" text-anchor="middle" dy=".3em" font-family="Arial" font-size="12" fill="#999">No Image</text></svg>')}`;
      image.alt = "No image available";
    }

    imageContainer.appendChild(image);

    // Product info
    const infoContainer = document.createElement("div");
    infoContainer.className = "product-recommendations-slider__product-info";

    const title = document.createElement("h3");
    title.className = "product-recommendations-slider__product-title";
    title.textContent = product.title;

    const priceContainer = document.createElement("div");
    priceContainer.className = "product-recommendations-slider__product-price";

    // Handle pricing
    if (product.compare_at_price_min > product.price_min) {
      // On sale
      const comparePrice = document.createElement("span");
      comparePrice.className =
        "product-recommendations-slider__product-price--compare";
      comparePrice.textContent = this.formatPrice(product.compare_at_price_min);
      priceContainer.appendChild(comparePrice);
    }

    const price = document.createElement("span");
    if (product.price_varies) {
      price.textContent = `From ${this.formatPrice(product.price_min)}`;
    } else {
      price.textContent = this.formatPrice(product.price);
    }
    priceContainer.appendChild(price);

    infoContainer.appendChild(title);
    infoContainer.appendChild(priceContainer);

    productCard.appendChild(imageContainer);
    productCard.appendChild(infoContainer);

    slide.appendChild(productCard);

    return slide;
  }

  /**
   * Format price using Shopify's money format
   */
  formatPrice(priceInCents) {
    const price = priceInCents / 100;
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: window.Shopify?.currency?.active || "USD",
    }).format(price);
  }

  /**
   * Initialize Swiper slider
   */
  initSwiper() {
    const swiperElement = this.element.querySelector(
      ".product-recommendations-slider__swiper"
    );

    if (!swiperElement) {
      console.error("Swiper element not found");
      return;
    }

    // Wait for Swiper to be available
    if (!window.Swiper) {
      setTimeout(() => this.initSwiper(), 100);
      return;
    }

    // Get settings from section settings or use defaults
    const autoplay = this.element.dataset.autoplay === "true";
    const autoplayDelay = parseInt(this.element.dataset.autoplayDelay) || 4000;

    this.swiper = new Swiper(swiperElement, {
      slidesPerView: "auto",
      spaceBetween: 20,
      loop: false,
      centeredSlides: false,
      centerInsufficientSlides: true,
      freeMode: false,
      watchSlidesProgress: true,
      autoplay: autoplay
        ? {
            delay: autoplayDelay * 1000,
            disableOnInteraction: false,
            pauseOnMouseEnter: true,
          }
        : false,

      // Navigation
      navigation: {
        nextEl: ".product-recommendations-slider__nav--next",
        prevEl: ".product-recommendations-slider__nav--prev",
      },

      // Pagination
      pagination: {
        el: ".product-recommendations-slider__pagination",
        clickable: true,
        renderBullet: function (index, className) {
          return `<span class="${className}" aria-label="Go to slide ${index + 1}"></span>`;
        },
      },

      // Responsive breakpoints
      breakpoints: {
        768: {
          spaceBetween: 24,
        },
        1024: {
          spaceBetween: 24,
        },
      },

      // Accessibility
      a11y: {
        enabled: true,
        prevSlideMessage: "Previous slide",
        nextSlideMessage: "Next slide",
        firstSlideMessage: "This is the first slide",
        lastSlideMessage: "This is the last slide",
        paginationBulletMessage: "Go to slide {{index}}",
      },

      // Keyboard navigation
      keyboard: {
        enabled: true,
        onlyInViewport: true,
      },

      // Mouse wheel
      mousewheel: {
        forceToAxis: true,
      },
    });
  }

  /**
   * Show loading state
   */
  showLoading() {
    this.loadingElement.style.display = "block";
    this.contentElement.style.display = "none";
    this.emptyElement.style.display = "none";
  }

  /**
   * Show content
   */
  showContent() {
    this.loadingElement.style.display = "none";
    this.contentElement.style.display = "block";
    this.emptyElement.style.display = "none";
  }

  /**
   * Show empty state
   */
  showEmptyState() {
    this.loadingElement.style.display = "none";
    this.contentElement.style.display = "none";
    this.emptyElement.style.display = "block";
  }

  /**
   * Destroy the component
   */
  destroy() {
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
  }
}

/**
 * Initialize all product recommendation sliders on the page
 */
document.addEventListener("DOMContentLoaded", function () {
  const sliders = document.querySelectorAll(".product-recommendations-slider");

  sliders.forEach((slider) => {
    new ProductRecommendationsSlider(slider);
  });
});

/**
 * Handle Shopify section editor events
 */
document.addEventListener("shopify:section:load", function (event) {
  const slider = event.target.querySelector(".product-recommendations-slider");
  if (slider) {
    new ProductRecommendationsSlider(slider);
  }
});

document.addEventListener("shopify:section:unload", function (event) {
  const slider = event.target.querySelector(".product-recommendations-slider");
  if (slider && slider.productRecommendationsSlider) {
    slider.productRecommendationsSlider.destroy();
  }
});

// Export for potential use in other scripts
if (typeof module !== "undefined" && module.exports) {
  module.exports = ProductRecommendationsSlider;
}
